import { useMemo } from "react"

import { format } from "date-fns"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { PropTypes } from "@l2r-front/l2r-proptypes"
import { useIsMobileDevice, Typography, useTheme } from "@l2r-front/l2r-ui"

import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { getHorizontalSigningConditionsValues } from "../../constants/horizontalSigningConditions"
import { useHorizontalSignType } from "../../hooks/queries/horizontalSigning/useHorizontalSignTypes"

import * as Styled from "./HorizontalSignDetails.styled"

export function HorizontalSignDetails(props) {

    const { horizontalSign } = props

    const { data: signType } = useHorizontalSignType(horizontalSign.horizontal_sign_type)
    const { t } = useTranslation()
    const theme = useTheme()
    const isMobile = useIsMobileDevice()

    const tagsChips = useMemo(() => {
        return (horizontalSign.tags || []).map(tag => ({ label: tag }))
    }, [horizontalSign])

    const updatedInfo = useMemo(() => {
        if (!horizontalSign.updated_at) {
            return null
        }

        const date = format(new Date(horizontalSign.updated_at), "dd/MM/yyyy")
        const campaign = horizontalSign.updated_by_campaign ?
            `"${horizontalSign.updated_by_campaign}"` : horizontalSign.updated_by_campaign_removed ? `"${t(I18N_NAMESPACE, "containers.horizontalSignDetails.deletedCampaign")}"`
                : null
        const responsable = campaign ?? horizontalSign.updated_by ?? t(I18N_NAMESPACE, "containers.horizontalSignDetails.logiroad")
        return <Typography variant="caption">
            {
                t(I18N_NAMESPACE, "containers.horizontalSignDetail.updateInfo", {
                    date,
                    user: responsable,
                })
            }
        </Typography>
    }, [horizontalSign, t])

    return <Styled.Container isMobile={isMobile}>
        <Styled.TitleHeader>
            <Typography variant="Desktop & Mobile/Heading/H3">{t(I18N_NAMESPACE, "containers.horizontalSignDetail.title")}</Typography>
        </Styled.TitleHeader>
        <Styled.TextWrapper>
            <Styled.Text isMobile={isMobile}>
                {t(I18N_NAMESPACE, "containers.horizontalSignDetail.condition.label")}&nbsp;
            </Styled.Text>
            <Styled.StateWrapper
                color={getHorizontalSigningConditionsValues(t, theme)[horizontalSign.condition].backgroundColor}
                isMobile={isMobile}
            >
                <Styled.State
                    color={getHorizontalSigningConditionsValues(t, theme)[horizontalSign.condition].labelColor}
                    variant="Desktop & Mobile/Heading/H3"
                >
                    {getHorizontalSigningConditionsValues(t, theme)[horizontalSign.condition].label}
                </Styled.State>
            </Styled.StateWrapper>
        </Styled.TextWrapper>
        {
            signType?.is_textual && <Styled.TextWrapper>
                <Styled.Text isMobile={isMobile}>
                    {t(I18N_NAMESPACE, "containers.horizontalSignDetail.panelText.label")}&nbsp;
                </Styled.Text>
                <Styled.ContentText
                    color={horizontalSign.content ? "text/primary" : "text/tertiary"}
                    isMobile={isMobile}
                >
                    {horizontalSign.content || t(I18N_NAMESPACE, "containers.horizontalSignDetail.panelText.text")}
                </Styled.ContentText>
            </Styled.TextWrapper>
        }
        <Styled.TextWrapper>
            <Styled.Text isMobile={isMobile}>
                {t(I18N_NAMESPACE, "containers.horizontalSignDetail.tags")}&nbsp;
            </Styled.Text>
            {tagsChips.length ? <Styled.ChipList isMobile={isMobile} values={tagsChips} />
                : <Styled.ContentText color={"text/tertiary"} isMobile={isMobile}>-</Styled.ContentText>}
        </Styled.TextWrapper>
        {updatedInfo}
    </Styled.Container>
}

HorizontalSignDetails.propTypes = {
    horizontalSign: PropTypes.horizontalSign.isRequired,
}